import { Switch } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import DeleteCardModal from "@/components/cards/CreditCard/DeleteCardModal.tsx";

import { CreditCardModel } from "@/api/user/user.types";
import { USER_KEYS, useUpdateCreditCard } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import { cn } from "@/utils/cn.ts";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

interface CreditCardProps {
  actions?: boolean;
  className?: string;
  creditCard: CreditCardModel;
}

const CreditCard = ({ actions = true, className, creditCard }: CreditCardProps): JSX.Element => {
  const { t } = useTranslation();

  const { mutate } = useUpdateCreditCard(creditCard.id);
  const [isLoading, setLoading] = useState(false);

  const queryClient = useQueryClient();
  const lang = useLang();

  const onUpdatePrimaryCard = (value: boolean) => {
    const formData = new FormData();

    formData.append("is_primary", value.toString());

    setLoading(true);

    mutate(
      { formData },
      {
        async onSuccess() {
          await queryClient.invalidateQueries({ queryKey: USER_KEYS.creditCards(lang) });

          onSuccess(t("cards.make-main-success"));

          setLoading(false);
        },
        onError(error) {
          setLoading(false);

          onError(error);
        },
      },
    );
  };

  return (
    <div
      className={cn(
        "flex aspect-[370/184] w-full flex-col justify-between rounded-xl p-3 outline outline-1 -outline-offset-1 outline-grey-1",
        className,
      )}
      style={{
        background:
          "linear-gradient(293deg, #F25001 -210.16%, #121212 81.16%), radial-gradient(126.75% 126.95% at -14.11% 147.09%, #F25001 0%, #080300 100%), linear-gradient(293deg, #F25001 -210.16%, #080300 81.16%), rgba(255, 255, 255, 0.20)",
      }}
    >
      <div className={"flex items-center justify-between"}>
        <div className={"flex items-center gap-2 pt-2"}>
          {creditCard.payment_system_image && <img className="h-6" src={creditCard.payment_system_image} />}
          <p className={"text-sm"}>{creditCard.card_payment_system.toUpperCase()}</p>
        </div>
        {actions ? <DeleteCardModal id={creditCard.id} /> : null}
      </div>
      <div>
        <div>
          <p className={"mb-3 text-2xl/normal font-medium max-lg:text-base/5"}>{creditCard.number}</p>
        </div>
        <div className={"flex items-end justify-between"}>
          {actions ? (
            <div className={"flex items-center gap-3"}>
              <p className={"text-sm/normal"}>{t("Payment.make-main")}</p>
              <Switch
                checked={creditCard.is_primary}
                disabled={isLoading}
                onChange={onUpdatePrimaryCard}
                className={cn(
                  "group relative flex h-6 w-10  cursor-pointer rounded-full bg-grey-1 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-orange-1 data-[focus]:outline-1 data-[focus]:outline-white",
                  { "animate-pulse": isLoading },
                )}
              >
                <span
                  aria-hidden="true"
                  className="group-data-[checked]: pointer-events-none inline-block size-4 translate-x-0 rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
                />
              </Switch>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CreditCard;
